﻿import { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useCompanies } from '@/hooks/useCompanies';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/components/common/Toast';
import { api } from '@/services/api';
import { config } from '@/config/env';

interface Props {
  show: boolean;
  preselectedAzienda?: string;
  onClose: () => void;
  onSaved: (codazienda: string, codcliente: string) => void;
}

export default function NewProspectModal({ show, preselectedAzienda, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { companies, fetchCompanies } = useCompanies();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    codazienda: preselectedAzienda || '0',
    ragsociale: '',
    indirizzo: '',
    cap: '',
    localita: '',
    regioncountry: '',
    stato: '',
    telefono: '',
    email: '',
  });

  useEffect(() => {
    if (show) {
      fetchCompanies();
      setForm(f => ({ ...f, codazienda: preselectedAzienda || '0' }));
    }
  }, [show, preselectedAzienda, fetchCompanies]);

  const resetForm = () => {
    setForm({
      codazienda: '0', ragsociale: '', indirizzo: '', cap: '',
      localita: '', regioncountry: '', stato: '', telefono: '', email: '',
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSave = async () => {
    if (form.codazienda === '0') {
      showToast('Errore', 'Selezionare un\'azienda', 'danger');
      return;
    }
    if (!form.ragsociale.trim()) {
      showToast('Errore', 'Inserire la ragione sociale', 'danger');
      return;
    }

    setSaving(true);
    try {
      // Generate prospect code
      const codcliente = 'P' + Date.now().toString(36).toUpperCase();

      const prospect: Record<string, string> = {
        camposearch: form.ragsociale,
        site: config.SITE,
        codazienda: form.codazienda,
        codcliente,
        ragsociale: form.ragsociale,
        tipologia: 'P',
        indirizzo: form.indirizzo,
        cap: form.cap,
        localita: form.localita,
        regioncountry: form.regioncountry,
        stato: form.stato,
        telefono: form.telefono,
        email: form.email,
        attivo: 'S',
      };

      // Add agent/capoarea code based on role
      const ruolo = localStorage.getItem('ruolo');
      if (ruolo === 'AGENTE' && user) {
        prospect.codagente = (user as any).codice || '';
      } else if (ruolo === 'CAPOAREA' && user) {
        prospect.capogruppo = (user as any).codice || '';
      }

      const response = await api.post<any>('crud/crud-api/create?recordType=clienti', [prospect]);

      showToast('Successo', 'Prospect creato con successo', 'success');

      // Get the codcliente from response if available
      let savedCodcliente = codcliente;
      if (response?.result?.length > 0) {
        savedCodcliente = response.result[0].record?.codcliente || codcliente;
      }

      resetForm();
      onSaved(form.codazienda, savedCodcliente);
    } catch {
      showToast('Errore', 'Errore nel salvataggio del Prospect', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const update = (field: string, value: string) => setForm(f => ({ ...f, [field]: value }));

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Nuovo Prospect</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Azienda *</Form.Label>
              <Form.Select value={form.codazienda} onChange={e => update('codazienda', e.target.value)}>
                <option value="0">{t('selectCompany')}</option>
                {companies.map(c => (
                  <option key={c.codice} value={c.codice}>{c.descrizione}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Ragione sociale *</Form.Label>
              <Form.Control value={form.ragsociale} onChange={e => update('ragsociale', e.target.value)} />
            </Form.Group>
          </Col>
          <Col md={9}>
            <Form.Group>
              <Form.Label>Indirizzo</Form.Label>
              <Form.Control value={form.indirizzo} onChange={e => update('indirizzo', e.target.value)} maxLength={150} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>CAP</Form.Label>
              <Form.Control value={form.cap} onChange={e => update('cap', e.target.value)} maxLength={10} />
            </Form.Group>
          </Col>
          <Col md={5}>
            <Form.Group>
              <Form.Label>Città</Form.Label>
              <Form.Control value={form.localita} onChange={e => update('localita', e.target.value)} maxLength={50} />
            </Form.Group>
          </Col>
          <Col md={5}>
            <Form.Group>
              <Form.Label>Regione</Form.Label>
              <Form.Control value={form.regioncountry} onChange={e => update('regioncountry', e.target.value)} maxLength={50} />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Stato</Form.Label>
              <Form.Control value={form.stato} onChange={e => update('stato', e.target.value)} maxLength={3} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Telefono</Form.Label>
              <Form.Control value={form.telefono} onChange={e => update('telefono', e.target.value)} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={form.email} onChange={e => update('email', e.target.value)} />
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>{t('actions.close')}</Button>
        <Button variant="success" onClick={handleSave} disabled={saving}>
          {saving ? '...' : t('actions.save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
